import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiPayloadCreator } from "util/api";
import { selectAuthToken } from "redux/features/auth";

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import deepEquals from "react-fast-compare";
import canonicalJSON from "canonicalize";

import {
  changesSlice,
  changesSubslice,
  updateChanges,
  clearChangedValue,
} from "redux/features/changes";
import { RESOURCE_TYPE_PARTICIPATION } from "redux/typeConstants";

import { profileSlice } from "redux/features/profile";

/* When a profile resource's data changes in a way that also impacts the
  main profile, perform the appropriate profile updates here. This is
  similar to the mechanism used in features/changes.js for other types
  of data that affect the profile but aren't accessed via a profileResources
  mechanism. */

const newDataHandlers = {};

// A participation is a separate resource but a summary of the participation
// is also present in the main profile. Update the summary when this
// information changes.
newDataHandlers[RESOURCE_TYPE_PARTICIPATION] = (data, personId, dispatch) => {
  // Fetch the aspects of the participation resource that go into the summary
  // (Skip this on return data for a non-existent activity, where there will
  // be no summary).
  if (!data.summary_fields) return;
  const summary = Object.fromEntries(
    data.summary_fields.map((field) => [field, data[field]])
  );

  // Dispatch updatePerson with the special "participation" subresource-update
  // key that will trigger the update of only that participation in the
  // participations list; see redux/features/profile.js
  dispatch(
    profileSlice.actions.updatePerson({
      public_id: personId,
      participation: summary,
    })
  );
};

function handleNewData(data, resourceType, personId, dispatch) {
  const handler = newDataHandlers[resourceType];
  if (handler) handler(data, personId, dispatch);
}

const apiCall = createAsyncThunk(
  "profileResources/apiCall",
  apiPayloadCreator(["api", "/"], "get", (success, arg, thunkAPI) => {
    const { resourceType, personId } = arg;
    handleNewData(success, resourceType, personId, thunkAPI.dispatch);
    return success;
  })
);

// A profile reource may be loaded with query parameters. Currently the only
// use for this is loading a "potential participation" record with a user-
// submitted discount code that might unlock certain discounted fee categories,
// but it could be used for other future purposes as well.

// The resource will occupy the same slice of state no matter what parameters
// are used to load it, so reloading a resource with different parameters
// will update any previously loaded version of the resource, and will retain
// any pending changes recorded for the resource.

// The set of parameters that were used to load the resource will be stored
// as "params" alongside the resource data, so a consumer can determine
// whether or not they are the desired parameters.

export const profileResourcesSlice = createSlice({
  name: "profileResources",
  initialState: {},
  reducers: {
    updatedData: (state, action) => {
      const { path, data } = action.payload;
      if (!state[path]) state[path] = {};
      state[path].pending = false;
      state[path].error = null;
      state[path].data = data;
      state[path].dataTime = Date.now();
      // Updating from an external source (e.g. different API call) is
      // considered to be a parameter-less load of the resource.
      state[path].params = null;
    },
  },
  extraReducers: {
    [apiCall.pending]: (state, action) => {
      const { arg } = action.meta;
      const { path } = arg;
      if (!state[path]) state[path] = {};
      state[path].pending = true;
      state[path].error = null;
    },
    [apiCall.fulfilled]: (state, action) => {
      const { arg } = action.meta;
      const { path, params } = arg;
      if (!state[path]) state[path] = {};
      state[path].pending = false;
      state[path].error = null;
      state[path].data = action.payload;
      state[path].dataTime = Date.now();
      state[path].params = params;
    },
    [apiCall.rejected]: (state, action) => {
      const { arg } = action.meta;
      const { path } = arg;
      if (!state[path]) state[path] = {};
      state[path].pending = false;
      state[path].error = action.payload || {
        message: "Unknown error",
      };
    },
  },
});

profileResourcesSlice.selector = (store) => store.profileResources;

// This "load" can be called with or without optional params; if called
// without params, it will reload with the most recently used set of
// parameters (if any). It will do nothing if a call is currently pending,
// so it can safely be put into a timer to refresh the resource periodically
// without worrying that the timer call will overlap a manual load call.
profileResourcesSlice.asyncActions = {
  load: ({ resourceType, resourceId, personId, params }) => (
    dispatch,
    getState
  ) => {
    const path = `${resourceType}/${resourceId}`;
    const state = profileResourcesSlice.selector(getState());
    if (!state[path]?.pending)
      dispatch(
        apiCall({
          resourceType,
          resourceId,
          personId,
          path,
          params: params === undefined ? state[path]?.params ?? null : params,
          selectAuthToken,
        })
      );
  },
};

export function useProfileResource(
  personId,
  resourceType,
  resourceId,
  section = resourceType
) {
  const resources = useSelector(profileResourcesSlice.selector);
  const path = `${resourceType}/${resourceId}`;
  // Return a loadState with "pending:true" if the initial load has not
  // yet even been initiated, but also set "uninitialized:true" in that
  // case as well, so we know we still have to initiazte a load!
  const resource = {
    loadState: resources[path] || { pending: true, uninitialized: true },
  };
  resource.data = resource.loadState.data;
  resource.changeRecord = changesSubslice(
    useSelector(changesSlice.selector),
    personId,
    section,
    resourceType,
    resourceId
  );
  resource.saveState = resource.changeRecord
    ? {
        pending: resource.changeRecord.pending,
        failed: resource.changeRecord.failed,
      }
    : {};
  const changeKeys = {
    id: personId,
    section,
    resourceType,
    resourceId,
  };

  const dispatch = useDispatch();
  resource.update = (field, value, equalityFunc = undefined) => {
    updateChanges(
      dispatch,
      changeKeys,
      field,
      resource.data[field],
      value,
      equalityFunc
    );
  };

  // Sometimes we may want to set a value for a profile resource that has not
  // yet been loaded, where we already know without having to load the full
  // resource what the currently stored value is. For example, this is used
  // when setting talk attendance in multiple activities as listed in
  // the items_needing_response section of a person's profile; we don't need
  // to first load the full participation resource for each activity merely
  // to set the "attending-talkID" value in it; we know the currently stored
  // value is null otherwise it wouldn't be an item needing response. The
  // following routine can be used in this case, as it doesn't require
  // resource.data to be present, just a specification of the previous value:
  resource.updateFrom = (field, value, previousValue) => {
    updateChanges(dispatch, changeKeys, field, previousValue, value);
  };

  resource.clearChangedValue = (field) => {
    clearChangedValue(dispatch, changeKeys, field);
  };

  resource.clear = () => {
    dispatch(changesSlice.actions.deleteChangeRecord(changeKeys));
  };

  resource.save = () => {
    if (resource.changeRecord)
      dispatch(changesSlice.asyncActions.save(changeKeys));
  };

  resource.reload = (params = undefined) => {
    dispatch(
      profileResourcesSlice.asyncActions.load({
        personId,
        resourceType,
        resourceId,
        params,
      })
    );
  };

  return resource;
}

export function useLoadProfileResource(
  personId,
  resourceType,
  resourceId,
  section = resourceType,
  reloadInterval = 60000,
  params = undefined
) {
  const resource = useProfileResource(
    personId,
    resourceType,
    resourceId,
    section
  );

  // Although we are returning the resource returned by useProfileResource,
  // which has a "reload" function attached, we don't use that function in
  // our reload-after-a-specific-interval timer, so as not to introduce a
  // complex dependency into useEffect. Instead, we build our own copy of
  // the action, as a known constant.

  // We also use a JSON canonicalization of params, so that the effect's
  // dependencies change only when the parameters actually change, not when
  // the object reference to them changes.
  const paramString = canonicalJSON(params ?? null);
  const haveWrongParams =
    !!resource.data &&
    params !== undefined &&
    !deepEquals(resource.loadState.params, params);

  const dispatch = useDispatch();
  const loadPending =
    resource.loadState.pending && !resource.loadState.uninitialized;
  const loadRequired = !loadPending && (!resource.data || haveWrongParams);

  useEffect(() => {
    // For the initial load, use the specified parameters (if any).
    // When setting a timer, leave params undefined so as to reload
    // with the currently effective parameters.
    const reloadArgs = { personId, resourceType, resourceId };
    const loadArgs = { ...reloadArgs, params: JSON.parse(paramString) };
    const load = profileResourcesSlice.asyncActions.load;

    const adjustedInterval = reloadInterval + Math.floor(Math.random() * 5000);
    if (loadRequired) dispatch(load(loadArgs));
    const timerId = window.setInterval(
      () => dispatch(load(reloadArgs)),
      adjustedInterval
    );
    return () => window.clearInterval(timerId);
  }, [
    dispatch,
    personId,
    resourceType,
    resourceId,
    reloadInterval,
    paramString,
    loadRequired,
  ]);
  return resource;
}

export function useLoadProfileResourceWithParams(
  personId,
  resourceType,
  resourceId,
  params,
  section = resourceType,
  reloadInterval = 60000
) {
  return useLoadProfileResource(
    personId,
    resourceType,
    resourceId,
    section,
    reloadInterval,
    params
  );
}

export function handleExternallyLoadedProfileResource({
  data,
  resourceType,
  resourceId,
  personId,
  dispatch,
}) {
  const path = `${resourceType}/${resourceId}`;
  dispatch(profileResourcesSlice.actions.updatedData({ data, path }));
  handleNewData(data, resourceType, personId, dispatch);
}

export default profileResourcesSlice.reducer;
